import { defineStore } from 'pinia'
import { DateTime } from 'luxon'
import { useNuxtApp } from '#imports'
import usePoolStore from '~/store/pool'
import useUserStore from '~/store/user'
import useVehiclesStore from '~/store/vehicles'
import type {
    ConnectorCompatibility,
    SimulationUserVehicle,
    VehicleToPoolCompatibility
} from '~/interfaces/simulation'
import { $fetchApi } from '~/composables/fetchApi'
import type { UserVehicle } from '#cmui/types/vehicle'
import type {
    ChargePriceSimulation,
    SimulationPoolConnector
} from '#cmui/types/simulation'
import type { PoolStationConnector } from '#cmui/types'

export const useSimulationStore = defineStore('simulation', () => {
    const meta = ref({
        isLoading: false,
        isError: false
    })
    const connectorCompatibilities = ref<ConnectorCompatibility[]>([])
    const priceDetails = ref<ChargePriceSimulation | undefined>(undefined)

    const simulator = reactive({
        currentVehicle: null as SimulationUserVehicle | null,
        userVehicles: [] as {
            vehicle: SimulationUserVehicle
            connectorCompatibilities: ConnectorCompatibility[]
        }[],
        currentConnectorId: null as number | null,
        chargeTime: '' as string,
        vehicleBatteryLevels: [20, 80] as number[]
    })

    const isPriceIncalculable = computed(() => {
        return (
            !simulator.currentVehicle?.isIncompatible &&
            !!simulator.currentVehicle &&
            !connectorCompatibilities.value.some(
                (connector) =>
                    connector.isPriceComputable && connector.isVehicleCompatible
            )
        )
    })

    const hasNoCompatibleVehicle = computed(() => {
        return (
            useUserStore().userVehicles?.length > 0 &&
            !connectorCompatibilities.value.some(
                (connector) => connector.isVehicleCompatible
            )
        )
    })

    const connectors = computed(() => {
        const { poolConnectors } = usePoolStore()

        return connectorCompatibilities.value
            .map((connector) => {
                const poolConnector = poolConnectors?.find(
                    (p) => p.id === connector.connectorId
                )

                if (!poolConnector) {
                    return null
                }

                return {
                    id: poolConnector.id,
                    overallState: poolConnector.overallState,
                    speed: poolConnector.speed,
                    power: poolConnector.power,
                    isUsable: poolConnector.isUsable,
                    realtimeStatus: poolConnector.realtimeStatus,
                    connectorType: poolConnector.connectorType,
                    currentType: poolConnector.currentType,
                    operatorName: poolConnector.operatorName,
                    isSelected:
                        connector.connectorId === simulator.currentConnectorId,
                    isIncompatible:
                        !connector.isPriceComputable ||
                        !connector.isVehicleCompatible,
                    remoteIdentifier: '' //not used here, so I'm setting it to mute all warnings
                } as (PoolStationConnector | SimulationPoolConnector) & {
                    isSelected: boolean
                    isIncompatible: boolean
                }
            })
            .filter((connector) => connector !== null)
            .sort((a, b) => Number(a.isIncompatible) - Number(b.isIncompatible))
    })

    const vehicles = computed(() => {
        // return user vehicles, with isSelected field, and sorted with default vehicle first, and incompatible vehicles last
        return simulator.userVehicles
            .map(({ vehicle }) => ({
                ...vehicle,
                isSelected:
                    vehicle.versionId === simulator.currentVehicle?.versionId &&
                    !vehicle.isIncompatible,
                isDefault: useUserStore().userVehicles.some(
                    (v) => v.isDefault && v.versionId === vehicle.versionId
                )
            }))
            .sort((a, b) => {
                if (a.isDefault) return -1

                if (b.isDefault) return 1

                return Number(a.isIncompatible) - Number(b.isIncompatible)
            })
    })

    const currentConnector = computed(() => {
        return (
            connectors.value.find(
                (connector) => connector.id === simulator.currentConnectorId
            ) || null
        )
    })

    const hasPriceDuration = computed(() => {
        return (
            connectorCompatibilities.value.find(
                (connector) =>
                    connector.connectorId === simulator.currentConnectorId
            )?.hasPriceDuration || false
        )
    })

    const hasIncompatibleVehicle = computed(() => {
        return simulator.userVehicles.some((uv) => uv.vehicle.isIncompatible)
    })

    const hasIncompatibleConnector = computed(() => {
        return connectors.value.some((connector) => connector.isIncompatible)
    })

    const isFreeCharge = computed(() => {
        return Number(priceDetails.value?.totalTaxedPrice.amount) === 0
    })

    function getUserVehicleId(vehicleVersionId: number) {
        return useUserStore().userVehicles.find(
            (v) => v.versionId === vehicleVersionId
        )?.id
    }

    async function findVehicleForSimulation() {
        const { userVehicles } = useUserStore()
        const { poolId } = usePoolStore()

        meta.value.isLoading = true
        meta.value.isError = false

        const assignVehicleData = async (
            vehicle: UserVehicle,
            isVehicleCompatible: boolean,
            newConnectorCompatibilities: ConnectorCompatibility[]
        ) => {
            connectorCompatibilities.value = newConnectorCompatibilities || []

            const selectedVehicle = {
                ...vehicle,
                isIncompatible: !isVehicleCompatible,
                isSelected: true
            }

            simulator.currentVehicle = selectedVehicle
            simulator.userVehicles = [
                {
                    vehicle: selectedVehicle,
                    connectorCompatibilities: newConnectorCompatibilities
                }
            ]

            if (connectorCompatibilities.value.length > 0) {
                simulator.currentConnectorId =
                    connectorCompatibilities.value.find(
                        (connector) => connector.isVehicleCompatible
                    )?.connectorId || null

                await fetchSimulationPriceDetails()
            }
        }

        const defaultVehicle = userVehicles?.find(
            (vehicle) => vehicle.isDefault
        )

        if (!userVehicles || userVehicles.length === 0) {
            meta.value.isLoading = false

            return
        }

        // if a default vehicle is already defined, we get its compatible connectors
        if (defaultVehicle) {
            const data = await checkVehiclePoolCompatibility(
                poolId,
                defaultVehicle.versionId
            )
            const { isVehicleCompatible, connectorCompatibilities } = data

            if (isVehicleCompatible) {
                // If the default vehicle is compatible, assign its data
                await assignVehicleData(
                    defaultVehicle,
                    isVehicleCompatible,
                    connectorCompatibilities
                )
            } else {
                // If the default vehicle is not compatible, loop over all user vehicles to find a compatible one
                for (const vehicle of userVehicles.filter(
                    (v) => !v.isDefault
                )) {
                    const data = await checkVehiclePoolCompatibility(
                        poolId,
                        vehicle.versionId
                    )

                    const { isVehicleCompatible, connectorCompatibilities } =
                        data

                    if (isVehicleCompatible) {
                        await assignVehicleData(
                            vehicle,
                            isVehicleCompatible,
                            connectorCompatibilities
                        )

                        break
                    }
                }

                // If no compatible vehicle is found, set the first user vehicle as the current vehicle
                if (!simulator.currentVehicle) {
                    await assignVehicleData(userVehicles[0]!, false, [])
                }
            }
        } else {
            // if no default vehicle is defined, loop over all user vehicles to find the first compatible vehicle
            for (const vehicle of userVehicles) {
                const data = await checkVehiclePoolCompatibility(
                    poolId,
                    vehicle.versionId
                )
                const { isVehicleCompatible, connectorCompatibilities } = data

                if (isVehicleCompatible) {
                    await assignVehicleData(
                        vehicle,
                        isVehicleCompatible,
                        connectorCompatibilities
                    )

                    break
                }
            }

            if (!simulator.currentVehicle) {
                await assignVehicleData(userVehicles[0]!, false, [])
            }
        }

        meta.value.isLoading = false
    }

    async function checkVehiclePoolCompatibility(
        poolId: number,
        versionId: number
    ): Promise<VehicleToPoolCompatibility> {
        return $fetchApi(
            `${useRuntimeConfig().public.APPLICATION_URL}/charge-simulator/charges`,
            {
                params: {
                    pool_id: poolId,
                    vehicle_version_id: versionId
                }
            }
        )
    }

    async function fetchSimulationPriceDetails() {
        const { $throwError } = useNuxtApp()

        const { poolId } = usePoolStore()

        meta.value.isLoading = true
        meta.value.isError = false

        try {
            // we set current date as start date if no charge time is defined
            const { chargeTime } = simulator

            const startDate = chargeTime
                ? DateTime.fromISO(
                      `${DateTime.now().toISODate()}T${chargeTime}:00`,
                      { zone: 'utc' }
                  ).toISO()
                : DateTime.now().toISO({ includeOffset: false }) + 'Z'

            const payload = {
                connector_id: simulator.currentConnectorId,
                vehicle_version_id: simulator.currentVehicle?.versionId,
                pool_id: poolId,
                battery_percents: {
                    min: simulator.vehicleBatteryLevels[0],
                    max: simulator.vehicleBatteryLevels[1]
                },
                start_date: startDate
            }

            const data: ChargePriceSimulation = await $fetchApi(
                `${useRuntimeConfig().public.APPLICATION_URL}/charge-simulator/charges`,
                {
                    method: 'POST',
                    body: JSON.stringify(payload)
                }
            )

            priceDetails.value = data
        } catch (e) {
            meta.value.isError = true
            $throwError(e, 'RequestFailed: fetchSimulationPriceDetails')
        } finally {
            meta.value.isLoading = false
        }
    }

    async function fetchVehiclesCompatibilities() {
        const { $throwError } = useNuxtApp()

        const { userVehicles } = useUserStore()
        const poolStore = usePoolStore()

        meta.value.isLoading = true
        meta.value.isError = false

        try {
            // check compatibility for all user vehicles, and add them to this store as SimulationUserVehicle in simulator.userVehicles
            await Promise.all(
                userVehicles?.map(async (vehicle: UserVehicle) => {
                    if (
                        simulator.userVehicles.some(
                            (v) => v.vehicle.versionId === vehicle.versionId
                        )
                    ) {
                        return
                    }

                    const data = await checkVehiclePoolCompatibility(
                        poolStore.poolId,
                        vehicle.versionId
                    )

                    const { isVehicleCompatible, connectorCompatibilities } =
                        data

                    const newVehicle: SimulationUserVehicle = {
                        ...vehicle,
                        isIncompatible: !isVehicleCompatible,
                        isSelected: false
                    }

                    simulator.userVehicles = [
                        ...simulator.userVehicles,
                        {
                            vehicle: newVehicle,
                            connectorCompatibilities:
                                connectorCompatibilities || []
                        }
                    ]
                }) || []
            )
        } catch (error) {
            meta.value.isError = true
            $throwError(error, 'RequestFailed: checkVehiclesCompatibility')
        } finally {
            meta.value.isLoading = false
        }
    }

    async function selectConnector(connectorId: number) {
        // if the selected connector is already the current connector, we do nothing
        if (simulator.currentConnectorId === connectorId) {
            return
        }
        simulator.currentConnectorId = connectorId

        await fetchSimulationPriceDetails()
    }

    async function selectVehicle(versionId: number) {
        // if the selected vehicle is already the current vehicle, we do nothing
        if (simulator.currentVehicle?.versionId === versionId) {
            return
        }

        // Otherwise, we set it as the current vehicle and set its compatible connectors as the current connectors
        simulator.currentVehicle =
            simulator.userVehicles.find(
                (v) => v.vehicle.versionId === versionId
            )?.vehicle || null

        connectorCompatibilities.value =
            simulator.userVehicles.find(
                (v) => v.vehicle.versionId === versionId
            )?.connectorCompatibilities || []

        // if the selected vehicle has compatible connectors, we set the first compatible one as the current connector
        if (connectorCompatibilities.value.length > 0) {
            simulator.currentConnectorId =
                connectorCompatibilities.value.find(
                    (connector) => connector.isVehicleCompatible
                )?.connectorId || null
        }

        await fetchSimulationPriceDetails()
    }

    async function setVehicleAsDefault(versionId: number) {
        const userStore = useUserStore()

        meta.value.isLoading = true

        const userVehicleId = getUserVehicleId(versionId)

        simulator.userVehicles = simulator.userVehicles.map(
            ({ vehicle, connectorCompatibilities }) => ({
                vehicle: {
                    ...vehicle,
                    isDefault: vehicle.versionId === versionId
                },
                connectorCompatibilities
            })
        )

        if (userVehicleId) {
            await userStore.setVehicleAsDefault(userVehicleId)
            selectVehicle(versionId)
        }

        meta.value.isLoading = false
    }

    async function deleteVehicle(versionId: number, updateUserStore = false) {
        const userStore = useUserStore()

        const userVehicleId = getUserVehicleId(versionId)

        if (userVehicleId) {
            await userStore.deleteVehicle(userVehicleId, updateUserStore)
            simulator.userVehicles = simulator.userVehicles.filter(
                ({ vehicle }) => vehicle.versionId !== versionId
            )
        }
    }

    async function submitVehicleForm(vehicle: UserVehicle) {
        const { $throwError } = useNuxtApp()
        const userStore = useUserStore()

        const { vehicleForModification } = useVehiclesStore()
        const { poolId } = usePoolStore()
        const { versionId } = vehicle

        meta.value.isLoading = true
        meta.value.isError = false

        try {
            // if a vehicle is being modified, we first delete the one that is being modified, and then we add the new vehicle (postVehicle)
            if (vehicleForModification) {
                deleteVehicle(vehicleForModification.versionId)
            }

            const data = await checkVehiclePoolCompatibility(poolId, versionId)
            const { isVehicleCompatible, connectorCompatibilities } = data

            simulator.userVehicles.push({
                vehicle: {
                    ...vehicle,
                    isIncompatible: !isVehicleCompatible,
                    isSelected: false,
                    isDefault: false
                },
                connectorCompatibilities
            })

            await userStore.postVehicle(versionId)

            if (isVehicleCompatible) {
                await selectVehicle(versionId)

                if (vehicle.isDefault) {
                    await setVehicleAsDefault(versionId)
                }
            }
        } catch (error) {
            meta.value.isError = true
            $throwError(error, 'RequestFailed: submitVehicleForm')
        } finally {
            meta.value.isLoading = false
        }
    }

    function reset() {
        meta.value.isLoading = false
        meta.value.isError = false
        connectorCompatibilities.value = []
        priceDetails.value = undefined
        simulator.currentVehicle = null
        simulator.userVehicles = []
        simulator.currentConnectorId = null
        simulator.chargeTime = ''
        simulator.vehicleBatteryLevels = [20, 80]
    }

    return {
        meta,
        connectorCompatibilities,
        priceDetails,
        simulator,
        isPriceIncalculable,
        hasNoCompatibleVehicle,
        connectors,
        vehicles,
        currentConnector,
        hasPriceDuration,
        hasIncompatibleVehicle,
        hasIncompatibleConnector,
        isFreeCharge,
        findVehicleForSimulation,
        checkVehiclePoolCompatibility,
        fetchSimulationPriceDetails,
        fetchVehiclesCompatibilities,
        selectConnector,
        selectVehicle,
        setVehicleAsDefault,
        deleteVehicle,
        submitVehicleForm,
        reset
    }
})

type SimulationStore = Omit<
    ReturnType<typeof useSimulationStore>,
    keyof ReturnType<typeof defineStore>
>

export default useSimulationStore
export type { SimulationStore }
